/**
 * Ported from student-dashboard-v2/src/types/simpleTypes.ts.
 * Backend-shaped types used directly (no GraphQL conversions), so the admin
 * preview lists and handles tests/questions exactly like the student website.
 */

export interface SimpleBlank {
  name: string; // "CHOICES1", "CHOICES2", etc.
  choices: string[];
  correctAnswer: string;
}

export interface SimpleMedia {
  type: string; // "VIDEO" | "IMAGE"
  url: string; // S3 key or URL
}

export interface SimplePayload {
  blanks?: SimpleBlank[];
  media?: SimpleMedia[];
}

export interface SimpleQuestion {
  _id: string;
  type: string; // "READ_SENTENCE", "COMPREHENSION", "MISSING_WORD", etc.
  name?: string;
  questionText: string;
  AiText?: string;
  description?: string;
  difficulty?: string;
  summary?: string;
  payload?: SimplePayload;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  isDeleted?: boolean;
  program?: {_id: string; name: string};
}

export interface SimpleTest {
  _id: string;
  type: 'DAILY_EXERCISE' | 'PLACEMENT_TEST';
  name?: string;
  description?: string;
  summary?: string;
  thumbnail?: string;
  questions?: SimpleQuestion[];
  media?: SimpleMedia[]; // Test-level media
  payload?: SimplePayload; // Exercise-level payload
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  isDeleted?: boolean;
  studentPlanLevelId?: string;
  studentPlanCurriculumId?: string;
}

export interface UIQuestion extends SimpleQuestion {
  testId: string;
  testType: 'DAILY_EXERCISE' | 'PLACEMENT_TEST';
  testName?: string;
  testThumbnail?: string;
  testDescription?: string;
  testMedia?: SimpleMedia[];
  studentPlanLevelId?: string;
  studentPlanCurriculumId?: string;

  // Media URLs (signed from S3)
  mediaUrl?: string;
  thumbnailUrl?: string;
  testMediaUrl?: string;
  testThumbnailUrl?: string;

  // UI state
  isAnswered?: boolean;
  currentQuestionIndex?: number;
  totalQuestionsInTest?: number;

  // Media type flags
  isVideo?: boolean;
  hasOwnMedia?: boolean;
  hasTestMedia?: boolean;

  testLevelMedia?: {
    isVideo: boolean;
    videoUrl?: string;
    thumbnailUrl?: string;
  };
  url?: string;
  image?: string;
  title?: string;
  text?: string;
  textAI?: string;
}

export interface QuestionCardData {
  id: string; // Composite: "testType-testId"
  testId: string;
  testType: 'DAILY_EXERCISE' | 'PLACEMENT_TEST';

  name?: string;
  title?: string;
  summary?: string;
  description?: string;

  url?: string;
  image?: string;
  thumbnailUrl?: string;
  mediaUrl?: string;
  isVideo: boolean;

  isBlur: boolean;
  showRedButton: boolean;
  answered: boolean;

  totalQuestionsInTest: number;
  currentQuestionIndex?: number;

  testLevelMedia?: {
    isVideo: boolean;
    videoUrl?: string;
    thumbnailUrl?: string;
  };

  text?: string;
  textAI?: string;
  payload?: SimplePayload;
  hasOwnMedia?: boolean;
  originalQuestionId?: string;
}

export function isPlacementTest(test: SimpleTest): boolean {
  return test.type === 'PLACEMENT_TEST';
}

export function isDailyExercise(test: SimpleTest): boolean {
  return test.type === 'DAILY_EXERCISE';
}

export function getMediaUrl(media?: SimpleMedia[]): string | undefined {
  return media && media.length > 0 ? media[0].url : undefined;
}

export function isVideoMedia(media?: SimpleMedia[]): boolean {
  return media && media.length > 0 && media[0].type?.toLowerCase() === 'video'
    ? true
    : false;
}
